import React, { useState } from "react";
import { ArrowRightLeft, ShieldOff, Bookmark, ChevronRight, Package } from "lucide-react";

const TABS = [
  {
    id: "transfer",
    label: "Transfer",
    icon: ArrowRightLeft,
    color: "blue",
    description: "Move stock from one location to another",
  },
  {
    id: "block",
    label: "Block",
    icon: ShieldOff,
    color: "red",
    description: "Quarantine stock — removes it from available qty",
  },
  {
    id: "reserve",
    label: "Reserve",
    icon: Bookmark,
    color: "amber",
    description: "Hold stock for a specific order or customer",
  },
];

const TAB_STYLES = {
  blue:  { active: "bg-blue-50 border-blue-500 text-blue-700",   btn: "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20",   icon: "text-blue-500 bg-blue-50",   badge: "bg-blue-100 text-blue-700"  },
  red:   { active: "bg-red-50 border-red-500 text-red-700",     btn: "bg-red-600 hover:bg-red-700 shadow-red-600/20",     icon: "text-red-500 bg-red-50",     badge: "bg-red-100 text-red-700"    },
  amber: { active: "bg-amber-50 border-amber-500 text-amber-700", btn: "bg-amber-500 hover:bg-amber-600 shadow-amber-500/20", icon: "text-amber-500 bg-amber-50", badge: "bg-amber-100 text-amber-700" },
};

function MaterialSelect({ materials, value, onChange, placeholder }) {
  const selected = materials.find((m) => m.id === value);
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all cursor-pointer pr-10"
      >
        <option value="">{placeholder}</option>
        {materials.map((m) => (
          <option key={m.id} value={m.id}>
            {m.values.materialNo || m.values.material_no || m.id} — {m.values.description || ""}
          </option>
        ))}
      </select>
      <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none" />
      {selected && (
        <div className="mt-2 px-3 py-2 bg-slate-100 rounded-lg flex items-center gap-2">
          <Package size={13} className="text-slate-400 shrink-0" />
          <span className="text-xs text-slate-600 font-medium truncate">
            Available: <strong>{selected.values.qty || 0}</strong> {selected.values.uom || "units"} · {selected.values.location || "no location"}
          </span>
        </div>
      )}
    </div>
  );
}

export default function TransferBlockReserve({
  materials,
  transfer, setTransfer,
  blockQty, setBlockQty,
  reserveQty, setReserveQty,
  onTransfer, onBlock, onReserve,
  onClose,
}) {
  const [activeTab, setActiveTab] = useState("transfer");
  const tab = TABS.find((t) => t.id === activeTab);
  const s = TAB_STYLES[tab.color];

  return (
    <div className="space-y-5">
      {/* Tab selector */}
      <div className="grid grid-cols-3 gap-2">
        {TABS.map((t) => {
          const isActive = activeTab === t.id;
          const ts = TAB_STYLES[t.color];
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                isActive
                  ? ts.active + " border-2"
                  : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? ts.icon : "bg-slate-100"}`}>
                <t.icon size={16} className={isActive ? "" : "text-slate-400"} />
              </div>
              <span className="text-xs font-bold">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Description */}
      <div className={`px-4 py-2.5 rounded-xl text-xs font-medium ${s.badge}`}>
        {tab.description}
      </div>

      {/* Transfer form */}
      {activeTab === "transfer" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Material</label>
            <MaterialSelect
              materials={materials}
              value={transfer.materialId}
              onChange={(v) => setTransfer({ ...transfer, materialId: v })}
              placeholder="Select material to transfer..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Destination</label>
              <input
                value={transfer.to}
                onChange={(e) => setTransfer({ ...transfer, to: e.target.value })}
                placeholder="e.g. MELBOURNE"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Quantity</label>
              <input
                type="number"
                min="0"
                value={transfer.qty}
                onChange={(e) => setTransfer({ ...transfer, qty: e.target.value })}
                placeholder="0"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-300 transition-all"
              />
            </div>
          </div>
          <button
            onClick={() => { onTransfer(); onClose?.(); }}
            disabled={!transfer.materialId || !transfer.to || !transfer.qty}
            className={`w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${s.btn}`}
          >
            <ArrowRightLeft size={16} /> Confirm Transfer
          </button>
        </div>
      )}

      {/* Block form */}
      {activeTab === "block" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Material</label>
            <MaterialSelect
              materials={materials}
              value={blockQty.materialId}
              onChange={(v) => setBlockQty({ ...blockQty, materialId: v })}
              placeholder="Select material to block..."
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Quantity to Block</label>
            <input
              type="number"
              min="0"
              value={blockQty.qty}
              onChange={(e) => setBlockQty({ ...blockQty, qty: e.target.value })}
              placeholder="0"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-300 transition-all"
            />
          </div>
          <button
            onClick={() => { onBlock(); onClose?.(); }}
            disabled={!blockQty.materialId || !blockQty.qty}
            className={`w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${s.btn}`}
          >
            <ShieldOff size={16} /> Block Stock
          </button>
        </div>
      )}

      {/* Reserve form */}
      {activeTab === "reserve" && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Material</label>
            <MaterialSelect
              materials={materials}
              value={reserveQty.materialId}
              onChange={(v) => setReserveQty({ ...reserveQty, materialId: v })}
              placeholder="Select material to reserve..."
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Quantity to Reserve</label>
            <input
              type="number"
              min="0"
              value={reserveQty.qty}
              onChange={(e) => setReserveQty({ ...reserveQty, qty: e.target.value })}
              placeholder="0"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-300 transition-all"
            />
          </div>
          <button
            onClick={() => { onReserve(); onClose?.(); }}
            disabled={!reserveQty.materialId || !reserveQty.qty}
            className={`w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${s.btn}`}
          >
            <Bookmark size={16} /> Reserve Stock
          </button>
        </div>
      )}
    </div>
  );
}
